import React from 'react'
import { connect } from 'react-redux'
import { Helmet } from 'react-helmet'
import styled from 'styled-components'
import { FaLink, FaCheckCircle } from 'react-icons/fa'
import { PulseLoader } from 'react-spinners'

// Assets
import invoiceIconImg from '../assets/img/icngrey.png'
import invoiceApprovedIconImg from '../assets/img/invoice-approved.png'

// CONSTANTS
import ROUTES from '../_constants/routes'
import colors, { tenantColors } from '../_constants/colors'

// Components
import { Note } from '../_components/Notes/Note'
import { Button } from '../_components/Button'

// Services
import { accountingPlatformsService } from '../_services'

// Actions
import { dealActions, userActions, accountingActions, alertActions } from '../_actions'
import { history } from '../_helpers'
import { formatCurrency } from '../_helpers/formatting'
import { COMPANY_NAME } from 'xen/constants'

// Styling
const StyledYourInvoicesPage = styled.div`
  .main-content {
    padding: 50px 0px;
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 800px;
    margin: 0 auto;
    width: 80%;

    & > * {
      margin-bottom: 25px;

      &:last-child {
        margin-bottom: 0px;
      }
    }

    .business-name,
    .note,
    .invoice-banner {
      width: 100%;
    }

    .invoice-banner {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      background-color: ${colors.GREY};
      border: 2px solid ${colors.GREY};
      border-radius: 5px;
      padding: 0.75em;
      color: #9eaeb6;
      font-size: 1.75rem;

      @media screen and (min-width: 700px) {
        flex-direction: row;
        text-align: left;
        justify-content: space-between;

        .main {
          display: flex;
          flex-direction: row;
          align-items: center;

          .invoice-icon {
            margin-right: 25px;
          }
        }

        .price {
          text-align: right;
        }
      }

      & > * {
        margin-bottom: 25px;

        @media screen and (min-width: 700px) {
          margin-bottom: 0px;

          & > * {
            margin-bottom: 5px;

            &:last-child {
              margin-bottom: 0px;
            }
          }
        }

        &:last-child {
          margin-bottom: 0px;
        }
      }

      .name,
      .price {
        color: black;
      }

      .number-date,
      .overdue {
        font-size: 0.55em;
      }
    }

    .loading-dots {
      padding: 50px 0px;
    }

    .loading-dots {
      transform: rotate(90deg);
    }

    .check-icon {
      color: ${colors.MATERIAL_GREEN};
      transform: translateY(2px);
    }
  }
`

const StyledDueDate = styled.p`
  color: ${(props) => (props.dueDate < props.today ? `red` : props.dueDate === props.today ? `orange` : `black`)};
`

const StyledInvoiceApprovedImg = styled.img`
  opacity: ${(props) => (props.loadingStep < 5 ? `0.25` : `1`)};
`

class YourInvoicesPage extends React.Component {
  state = {
    loading: true,
    loading_step: 1,
    activeInvoices: {},
    numInvoices: [],
    pendingInvoices: {},
    invoicesToImport: [],
    totalFaceValue: 0,
    numImported: 0,
    activeInvoice: {},
    submittedPreAppInvoices: [],
    pendingPreAppInvoices: [],
    submittedNonPreAppInvoices: [],
    pendingNonPreAppInvoices: [],
    invoiceImportDone: false,
  }

  componentDidMount() {
    const {
      user,
      business,
      deal,
      createDeal,
      setDeal,
      updateDeal,
      updateUser,
      activeCustomers,
      setActiveCustomers,
      addActiveCustomer,
      updateActiveCustomer,
      showAlert,
      platform,
    } = this.props

    // pull invoices from current accouting platform
    accountingPlatformsService.getLatestInvoices(business.id, platform).then(
      (resp) => {
        let latestInvoices = resp

        this.setState({
          numInvoices: latestInvoices.length,
        })

        latestInvoices.forEach((invoice) => {
          // formatting invoice attributes
          invoice.selected = false
          this.setState({
            activeInvoices: latestInvoices,
          })
        })

        // now figure out which of the accounting invoices have been imported
        // previously so that they are not imported again
        accountingPlatformsService.getAllSubmittedInvoices(business.id).then((resp) => {
          const prevSubmittedInvoices = resp.invoices
          let debtorNames = []

          latestInvoices.forEach((invoice) => {
            const submittedIndex = prevSubmittedInvoices.findIndex(
              (i) => i.external_reference_number === invoice.external_invoice_id.toString()
            )

            if (submittedIndex >= 0) {
              invoice.submitted = true
            } else {
              invoice.submitted = false
            }

            // store debtor names to check for preapproval or late payments
            debtorNames.push(invoice.debtor_name)
          })
          this.setState({
            activeInvoices: latestInvoices,
          })

          // getBanners

          // now get any pending invoices
          accountingPlatformsService.getAllPendingInvoices(business.id).then((resp) => {
            const pendingInvoices = resp
            let invoicesToImportCopy = [...this.state.invoicesToImport]
            latestInvoices.forEach((invoice) => {
              const pendingIndex = pendingInvoices.findIndex(
                (i) => i.external_reference_number === invoice.external_invoice_id.toString()
              )

              if (pendingIndex >= 0) {
                invoice.pending = true
                this.setState({
                  activeInvoices: latestInvoices,
                })

                // if invoice has been removed then import it
                if (invoice.removed) {
                  invoicesToImportCopy.push(invoice)
                  this.setState({
                    invoicesToImport: invoicesToImportCopy,
                  })
                }
              } else {
                invoice.pending = false
                this.setState({
                  activeInvoices: latestInvoices,
                })

                // if invoice isn't pending and isn't submitted, then it needs be imported
                if (!invoice.submitted) {
                  invoicesToImportCopy.push(invoice)
                  this.setState({
                    invoicesToImport: invoicesToImportCopy,
                  })
                }
              }
            })

            let faceValues = []
            let totalFaceValue = 0

            latestInvoices.forEach((invoice) => {
              faceValues.push(parseFloat(invoice.face_value))
              if (invoice.face_value) {
                totalFaceValue += parseFloat(invoice.face_value)
              }
            })

            this.setState({
              totalFaceValue: totalFaceValue,
            })

            const uniqDebtorNames = [...new Set(debtorNames)]
            uniqDebtorNames.map((uniqueName) => {
              let numCustomerInvoices = 0
              let totalCustomerFaceValue = 0
              debtorNames.map((name, i) => {
                if (name === uniqueName) {
                  numCustomerInvoices++
                  if (faceValues[i]) {
                    totalCustomerFaceValue += faceValues[i]
                  }
                }
              })

              const currentCustomer = {
                name: uniqueName,
                numInvoices: numCustomerInvoices,
                faceValue: totalCustomerFaceValue,
              }
              if (activeCustomers.filter((cust) => cust.name == currentCustomer.name).length == 0) {
                addActiveCustomer(currentCustomer)
              }
            })

            // keep track of when array is filled (for animation purposes)
            this.setState({
              prevInvoiceCheckDone: true,
            })

            const lookupObj = {
              debtorNames: uniqDebtorNames,
            }

            if (this.state.invoicesToImport.length > 0) {
              accountingPlatformsService.checkPreapproval(lookupObj).then((resp) => {
                const preApprovedDebtorNames = resp.preapproved_debtor_names
                let preapprovedIndices = []

                this.setState({
                  preApprovedDebtorNames: preApprovedDebtorNames,
                })

                let invoicesToImportCopy = [...this.state.invoicesToImport]
                let preapprovedInvoices = []
                invoicesToImportCopy.forEach((invoice) => {
                  const preapprovedIndex = preApprovedDebtorNames.findIndex((n) => n === invoice.debtor_name)

                  if (preapprovedIndex >= 0) {
                    invoice.preapproved = true
                    preapprovedInvoices.push(invoice)
                    this.setState({
                      preapprovedInvoices: preapprovedInvoices,
                    })
                  } else {
                    invoice.preapproved = false
                  }

                  this.setState({
                    invoicesToImport: invoicesToImportCopy,
                  })
                })

                let activeCustomersCopy = [...activeCustomers]
                activeCustomersCopy.map((customer, i) => {
                  let numCustomerPreapprovedInvoices = 0
                  this.state.invoicesToImport.forEach((invoice) => {
                    if (invoice.debtor_name === customer.name) {
                      numCustomerPreapprovedInvoices += invoice.preapproved
                    }
                  })
                  customer.preapproved = numCustomerPreapprovedInvoices > 0
                  customer.numPreAppInvoices = numCustomerPreapprovedInvoices
                  updateActiveCustomer(customer, i)
                  // setActiveCustomers(activeCustomersCopy)
                })

                this.setState({
                  preApprovedCheckDone: true,
                })

                // now create deal and import the appropriate the invoices...
                if (deal.deal_status === undefined || deal.deal_status === `submitted`) {
                  // createDeal

                  createDeal(business.id).then((resp) => {
                    const deal = {
                      ...resp,
                      creationDate: resp.created_at,
                      totalFaceValue: this.state.totalFaceValue,
                      totalNumInvoices: this.state.invoicesToImport.length,
                      totalPreAppInvoices: this.state.preapprovedInvoices.length,
                      totalPreAppCustomers: this.state.preapprovedDebtorNames.length,
                    }

                    setDeal(deal)

                    this.importInvoices()
                  })
                } else {
                  this.importInvoices()
                }
              })
            } else {
              this.setState({
                loading_step: 0,
              })
              showAlert({
                title: `No new invoices were found`,
                message: `No new invoices were found in your quickbooks account at this time.`,
                onConfirm: () => {
                  history.push(ROUTES.upload_invoices)
                },
                showCancelButton: false,
              })
            }
          })
        })
      },
      (error) => {
        showAlert({
          title: `Something went wrong...`,
          message: `We could not connect to an account. Please try connecting an account.`,
          onConfirm: () => {
            history.push(ROUTES.accounting_platforms)
          },
          showCancelButton: false,
        })
      }
    )
  }

  importInvoices = () => {
    const { user, business, deal, createDeal, setDeal, updateDeal, updateUser, platform } = this.props
    const messages = [
      `Pulling more info about your selected invoices`,
      `Looking up supporting documents for your invoices`,
    ]

    const timeMessage1 = 0
    const timeMessage2 = this.state.invoicesToImport.length * 2000
    const times = [timeMessage1, timeMessage2]

    // store the last external invoice id to track when all import invoice
    // api calls have completed
    const lastExternalInvoiceId =
      this.state.invoicesToImport[this.state.invoicesToImport.length - 1].external_invoice_id

    let debtor_ids = []
    let external_invoice_ids = []
    let uploadInvoicesArr = []

    // now import all invoices, one by one, updating vm.activeInvoice
    // which in turn animates the visible card on the page w each invoice
    this.state.invoicesToImport.forEach((invoice) => {
      const accountingInvoice = invoice
      accountingPlatformsService
        .importInvoice(business.id, accountingInvoice.external_invoice_id, accountingInvoice.preapproved, platform)
        .then((resp) => {
          let numImportedCopy = this.state.numImported
          numImportedCopy++
          this.setState({
            numImported: numImportedCopy,
          })

          const totalInvoices = this.state.invoicesToImport.length

          if (this.state.numImported === totalInvoices) {
            this.setState({
              loading_step: 5,
            })
          } else if (this.state.numImported === 3 * Math.round(totalInvoices / 4)) {
            this.setState({
              loading_step: 4,
            })
          } else if (this.state.numImported === 2 * Math.round(totalInvoices / 4)) {
            this.setState({
              loading_step: 3,
            })
          } else if (this.state.numImported === Math.round(totalInvoices / 4)) {
            this.setState({
              loading_step: 2,
            })
          }

          const activeInvoice = {
            ...resp.invoice,
            debtor_name: resp.debtor.debtor_name,
            business_debtor_id: resp.business_debtor.id,
            advanceAmount: [``, ``, 0],
            document_issue_date: resp.invoice_document.document_issue_date,
            document_number: resp.invoice_document.document_number,
            deal_id: deal.id,
            debtor_id: resp.debtor.id,
            external_invoice_id: resp.external_invoice_id,
          }

          this.setState({
            activeInvoice: activeInvoice,
          })

          // needed for uploading docs from accounting platform
          debtor_ids.push(resp.debtor.id)
          external_invoice_ids.push(resp.external_invoice_id)
          uploadInvoicesArr.push({
            document_id: resp.invoice_document.id,
            external_reference_number: resp.external_invoice_id,
          })

          // add imported invoice to appropriate datasharingservice array
          if (activeInvoice.preapproved) {
            if (activeInvoice.submitted) {
              let submittedPreAppInvoicesCopy = [...this.state.submittedPreAppInvoices]
              submittedPreAppInvoicesCopy.push(activeInvoice)
              this.setState({
                submittedPreAppInvoices: submittedPreAppInvoicesCopy,
              })
            } else {
              let pendingPreAppInvoicesCopy = [...this.state.pendingPreAppInvoices]
              pendingPreAppInvoicesCopy.push(activeInvoice)
              this.setState({
                pendingPreAppInvoices: pendingPreAppInvoicesCopy,
              })
            }
          } else {
            if (activeInvoice.submitted) {
              let submittedNonPreAppInvoicesCopy = [...this.state.submittedNonPreAppInvoices]
              submittedNonPreAppInvoicesCopy.push(activeInvoice)
              this.setState({
                submittedNonPreAppInvoices: submittedNonPreAppInvoicesCopy,
              })
            } else {
              let pendingNonPreAppInvoicesCopy = [...this.state.pendingNonPreAppInvoices]
              pendingNonPreAppInvoicesCopy.push(activeInvoice)
              this.setState({
                pendingNonPreAppInvoices: pendingNonPreAppInvoicesCopy,
              })
            }
          }

          // update deal status appropriately
          if (this.state.submittedPreAppInvoices.length + this.state.submittedNonPreAppInvoices.length > 0) {
            updateDeal(`deal_status`, `pending_and_submitted`, deal.id)
          } else {
            updateDeal(`deal_status`, `pending`, deal.id)
          }

          let lastId
          if (resp.external_invoice_id === lastExternalInvoiceId) {
            lastId = resp.invoice.id
          }

          accountingPlatformsService.linkInvoiceToDeal(this.state.activeInvoice.id, deal.id).then((resp) => {
            if (resp.id === lastId) {
              // now upload invoice and report documents from accounting platform
              const uploadDocumentsDataObj = {
                debtor_ids: debtor_ids,
                external_invoice_ids: external_invoice_ids,
                invoices_to_upload: uploadInvoicesArr,
              }

              accountingPlatformsService
                .uploadAccountingInvoicesAndReports(user.id, deal.id, uploadDocumentsDataObj, platform)
                .then((resp) => {
                  this.setState({
                    invoiceImportDone: true,
                  })
                })

              // record flow step
              if (user.flowStep === `redirected_to_first_tab`) {
                updateUser(`flow_step`, `invoices_added_by_new_user`, user.id)
              }
            }
          })
        })
    })
  }

  render() {
    const { user, business, tenant, activeCustomers } = this.props
    const {
      invoicesToImport,
      activeInvoice,
      loading_step,
      preApprovedDebtorNames,
      preapprovedInvoices,
      invoiceImportDone,
      preApprovedCheckDone,
      totalFaceValue,
    } = this.state
    const activeTenantColors = tenantColors[tenant]
    return (
      <StyledYourInvoicesPage activeTenantColors={activeTenantColors}>
        <Helmet>
          <title>Your Invoices • {COMPANY_NAME}</title>
        </Helmet>
        <div className={`main-content`}>
          <h1 className={`business-name`}>{business.business_name || user.email_address}</h1>
          <Note className={`note`} icon={FaLink} message={`You're connected with Quickbooks.`}></Note>
          <p>Latest Invoices ({invoicesToImport.length}):</p>
          {activeInvoice.debtor_name && (
            <div className={`invoice-banner`}>
              <div className={`main`}>
                <img alt="" className={`invoice-icon`} src={invoiceIconImg} />
                <div>
                  <p className={`name`}>{activeInvoice.debtor_name}</p>
                  <p className={`number-date`}>
                    Invoice #{activeInvoice.document_number} | Issued {activeInvoice.document_issue_date}
                  </p>
                </div>
              </div>
              <div>
                <p className={`price`}>{formatCurrency(activeInvoice.face_value)}</p>
                <StyledDueDate
                  className={`overdue`}
                  dueDate={new Date(activeInvoice.debtor_due_date)}
                  today={new Date()}
                >
                  Due Date: {activeInvoice.debtor_due_date}
                </StyledDueDate>
              </div>
            </div>
          )}
          {loading_step < 5 && (
            <div className={`loading-dots`}>
              <PulseLoader color={tenantColors[tenant].secondary} loading={true} size={20} />
            </div>
          )}
          <StyledInvoiceApprovedImg
            alt={``}
            className={`invoice-approved-img`}
            loadingStep={loading_step}
            src={invoiceApprovedIconImg}
          />
          {loading_step < 5 ? (
            <p>Loading Latest Invoices...</p>
          ) : (
            <div>
              <p>
                <FaCheckCircle className={`icon check-icon`} /> We found {invoicesToImport.length} invoice(s) from{' '}
                {activeCustomers.length} customer(s), totalling {formatCurrency(totalFaceValue)}.
              </p>
              {preApprovedDebtorNames.length > 0 && (
                <p>
                  <FaCheckCircle className={`icon check-icon`} /> {preapprovedInvoices.length} invoice(s) from{' '}
                  {preApprovedDebtorNames.length} customer(s) are Preapproved!
                </p>
              )}
            </div>
          )}
          {invoiceImportDone && preApprovedCheckDone && (
            <Button buttonType={`link`} text={`Continue`} to={ROUTES.confirm_form} />
          )}
        </div>
      </StyledYourInvoicesPage>
    )
  }
}

function mapStateToProps(state) {
  const { user, business, env, deal, accounting } = state
  return {
    user,
    business,
    tenant: env.tenant,
    deal: deal.deal,
    activeCustomers: accounting.activeCustomers,
    platform: accounting.platform,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    createDeal: (businessId) => {
      dispatch(dealActions.createDeal(businessId))
    },
    setDeal: (deal) => {
      dispatch(dealActions.setDeal(deal))
    },
    updateDeal: (attr, val, dealId) => {
      dispatch(dealActions.updateDeal(attr, val, dealId))
    },
    updateUser: (attr, val, userId) => {
      dispatch(userActions.updateUser(attr, val, userId))
    },
    setActiveCustomers: (customers) => {
      dispatch(accountingActions.setActiveCustomers(customers))
    },
    addActiveCustomer: (customer) => {
      dispatch(accountingActions.addActiveCustomer(customer))
    },
    updateActiveCustomer: (customer, index) => {
      dispatch(accountingActions.updateActiveCustomer(customer, index))
    },
    showAlert: (alert) => {
      dispatch(alertActions.showAlert(alert))
    },
  }
}

const connectedYourInvoicesPage = connect(mapStateToProps, mapDispatchToProps)(YourInvoicesPage)
export { connectedYourInvoicesPage as YourInvoicesPage }
